/**
 * Resend Email Service
 * Handles all transactional, promotional, and reminder emails via Supabase Edge Functions.
 * Respects user communication settings preferences before sending.
 * All functions fail silently — email sending never blocks the main user flow.
 */

import { supabase } from '../lib/supabase';
import { gaTrackNotificationReceived } from './gaAnalyticsService';

const TRANSACTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;
const PROMOTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-promotional-emails`;

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const getAuthHeaders = async () => {
  try {
    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return headers;
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

const callEdgeFunction = async (url, type, data) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, data }),
    });
    if (!response?.ok) {
      const err = await response?.json()?.catch(() => ({}));
      console.warn(`[ResendEmail] Failed "${type}":`, err?.error || response?.statusText);
      return { success: false, error: err?.error };
    }
    const result = await response?.json();
    return { success: true, data: result };
  } catch (err) {
    console.warn(`[ResendEmail] Error "${type}":`, err?.message);
    return { success: false, error: err?.message };
  }
};

// ─── COMMUNICATION SETTINGS CHECK ─────────────────────────────────────────────

/**
 * Fetch user communication settings from Supabase.
 * Returns defaults (all enabled) if no settings found.
 */
export const getUserCommunicationSettings = async (userId) => {
  if (!userId) return null;
  try {
    const { data } = await supabase
      ?.from('communication_settings')
      ?.select('email_enabled, email_reminders, email_reminders_freq, email_promotions, email_promotions_freq, email_transactional, email_transactional_freq')
      ?.eq('user_id', userId)
      ?.maybeSingle();
    return data;
  } catch {
    return null;
  }
};

/**
 * Check if a specific email category is allowed for a user.
 * @param {string} userId
 * @param {'transactional'|'promotions'|'reminders'} category
 * @returns {Promise<boolean>}
 */
export const isEmailAllowed = async (userId, category) => {
  if (!userId) return true; // Anonymous users — allow
  const settings = await getUserCommunicationSettings(userId);
  if (!settings) return true; // No settings row — default allow
  if (settings?.email_enabled === false) return false; // Master off
  if (category === 'transactional') return settings?.email_transactional !== false;
  if (category === 'promotions') return settings?.email_promotions !== false;
  if (category === 'reminders') return settings?.email_reminders !== false;
  return true;
};

/**
 * Get the effective frequency for a category.
 * @param {object} settings - communication_settings row
 * @param {'transactional'|'promotions'|'reminders'} category
 * @returns {'immediate'|'daily'|'weekly'|'never'}
 */
export const getEmailFrequency = (settings, category) => {
  if (!settings) return 'immediate';
  if (category === 'transactional') return settings?.email_transactional_freq || 'immediate';
  if (category === 'promotions') return settings?.email_promotions_freq || 'weekly';
  if (category === 'reminders') return settings?.email_reminders_freq || 'immediate';
  return 'immediate';
};

// ─── TRANSACTIONAL EMAILS ──────────────────────────────────────────────────────

/**
 * Send appointment confirmation email.
 * Respects user's transactional email preference.
 */
export const sendAppointmentConfirmationEmail = async (params) => {
  const { user_id } = params;
  if (!(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Appointment confirmation skipped — user preference off');
    return { success: false, skipped: true };
  }
  const result = await callEdgeFunction(TRANSACTIONAL_URL, 'appointment_confirmation', params);
  if (result?.success) {
    // Track in GA — Funnel Stage 3 (email notification dispatched)
    gaTrackNotificationReceived({ channel: 'email', type: 'appointment_confirmation', propertyId: params?.property_id });
  }
  return result;
};

/**
 * Send property inquiry acknowledgement email.
 * Respects user's transactional email preference.
 */
export const sendPropertyInquiryEmail = async (params) => {
  const { user_id } = params;
  if (!(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Property inquiry email skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'property_inquiry', params);
};

/**
 * Send notary appointment confirmation email.
 * Respects user's transactional email preference.
 */
export const sendNotaryAppointmentEmail = async (params) => {
  const { user_id } = params;
  if (!(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Notary appointment email skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'notary_appointment', params);
};

// ─── REMINDER EMAILS ───────────────────────────────────────────────────────────

/**
 * Send appointment reminder email (24h or 1h before).
 * Respects user's reminders email preference.
 * @param {object} params
 * @param {string} params.user_id
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} params.appointment_type
 * @param {string} params.scheduled_date
 * @param {string} [params.scheduled_time]
 * @param {string} [params.location]
 * @param {string} [params.notary_name]
 * @param {string} [params.consultant_name]
 * @param {'24h'|'1h'} [params.reminder_type]
 */
export const sendAppointmentReminderEmail = async (params) => {
  const { user_id } = params;
  if (!(await isEmailAllowed(user_id, 'reminders'))) {
    console.info('[ResendEmail] Appointment reminder skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(PROMOTIONAL_URL, 'appointment_reminder', params);
};

// ─── PROMOTIONAL EMAILS ────────────────────────────────────────────────────────

/**
 * Send wishlist saved confirmation email (triggered when user saves a property).
 * Respects user's transactional email preference.
 * @param {object} params
 * @param {string} params.user_id
 * @param {string} params.user_name
 * @param {string} params.user_email
 * @param {string} params.property_title
 * @param {number} [params.property_price]
 * @param {string} [params.property_commune]
 * @param {string} [params.property_type]
 * @param {string} [params.property_url]
 */
export const sendWishlistSavedEmail = async (params) => {
  const { user_id } = params;
  if (!user_id || !params?.user_email) return { success: false, skipped: true };
  if (!(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Wishlist saved email skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(PROMOTIONAL_URL, 'wishlist_saved', params);
};

/**
 * Send property update alert email (price drop, new listing, status change).
 * Respects user's transactional email preference.
 * @param {object} params
 * @param {string} params.user_id
 * @param {string} params.user_name
 * @param {string} params.user_email
 * @param {string} params.property_title
 * @param {string} [params.property_commune]
 * @param {'price_drop'|'new_listing'|'status_change'} params.update_type
 * @param {string} [params.update_description]
 * @param {string} [params.property_url]
 */
export const sendPropertyUpdateEmail = async (params) => {
  const { user_id } = params;
  if (!user_id || !params?.user_email) return { success: false, skipped: true };
  if (!(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Property update email skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(PROMOTIONAL_URL, 'property_update', params);
};

/**
 * Send a promotional campaign email to a user.
 * Respects user's promotions email preference.
 * @param {object} params
 * @param {string} params.user_id
 * @param {string} params.user_name
 * @param {string} params.user_email
 * @param {string} [params.campaign_title]
 * @param {string} [params.campaign_body]
 * @param {string} [params.campaign_cta_label]
 * @param {string} [params.campaign_cta_url]
 * @param {Array}  [params.featured_properties] - [{title, commune, price}]
 */
export const sendPromotionalCampaignEmail = async (params) => {
  const { user_id } = params;
  if (!user_id || !params?.user_email) return { success: false, skipped: true };
  if (!(await isEmailAllowed(user_id, 'promotions'))) {
    console.info('[ResendEmail] Promotional campaign skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(PROMOTIONAL_URL, 'promotional_campaign', params);
};

/**
 * Batch send a promotional campaign to multiple users.
 * Each user's preferences are checked individually.
 * @param {Array<object>} recipients - array of user objects with user_id, user_email, user_name
 * @param {object} campaignData - campaign_title, campaign_body, campaign_cta_label, campaign_cta_url, featured_properties
 * @returns {Promise<{sent: number, skipped: number, failed: number}>}
 */
export const sendBatchPromotionalCampaign = async (recipients, campaignData) => {
  let sent = 0, skipped = 0, failed = 0;

  const results = await Promise.allSettled(
    recipients?.map(async (recipient) => {
      const result = await sendPromotionalCampaignEmail({
        ...campaignData,
        user_id: recipient?.user_id || recipient?.id,
        user_email: recipient?.user_email || recipient?.email,
        user_name: recipient?.user_name || recipient?.full_name || recipient?.email,
      });
      return result;
    })
  );

  results?.forEach((r) => {
    if (r?.status === 'fulfilled') {
      if (r?.value?.skipped) skipped++;
      else if (r?.value?.success) sent++;
      else failed++;
    } else {
      failed++;
    }
  });

  console.info(`[ResendEmail] Batch campaign: sent=${sent}, skipped=${skipped}, failed=${failed}`);
  return { sent, skipped, failed };
};

/**
 * Send application decision email (approval or rejection) to a professional applicant.
 * Includes inline feedback, decision status badge, commission rate (if approved), and next steps.
 * @param {object} params
 * @param {string} params.applicant_name
 * @param {string} params.applicant_email
 * @param {'approved'|'rejected'} params.decision
 * @param {string} [params.category] - Professional category label
 * @param {string} [params.feedback_message] - Inline feedback from admin
 * @param {number} [params.commission_rate] - Commission rate (approval only)
 */
export const sendApplicationDecisionEmail = async (params) => {
  if (!params?.applicant_email) {
    console.warn('[ResendEmail] sendApplicationDecisionEmail: no email provided');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'application_decision', params);
};

/**
 * Send appointment rescheduled notification email.
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.appointment_type
 * @param {string} params.new_date - ISO date string
 * @param {string} [params.new_time]
 * @param {string} [params.old_date] - ISO date string of previous appointment
 * @param {string} [params.notary_name]
 * @param {string} [params.consultant_name]
 * @param {string} [params.location]
 */
export const sendAppointmentRescheduledEmail = async (params) => {
  const { user_id, client_email } = params;
  if (!client_email) return { success: false, skipped: true };
  if (user_id && !(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Rescheduled notification skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'appointment_rescheduled', params);
};

/**
 * Send appointment status update email (confirmed, cancelled, completed, rescheduled).
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.appointment_type
 * @param {string} params.scheduled_date
 * @param {string} [params.scheduled_time]
 * @param {'confirmed'|'cancelled'|'completed'|'rescheduled'} params.new_status
 * @param {string} [params.notary_name]
 * @param {string} [params.consultant_name]
 * @param {string} [params.location]
 * @param {string} [params.admin_note]
 */
export const sendAppointmentStatusUpdateEmail = async (params) => {
  const { user_id, client_email } = params;
  if (!client_email) return { success: false, skipped: true };
  if (user_id && !(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Status update notification skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'appointment_status_update', params);
};

/**
 * Send inquiry response notification email.
 * Triggered when an agent responds to a client's property inquiry.
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.property_title
 * @param {string} [params.property_type]
 * @param {string} [params.inquiry_message] - Original inquiry message
 * @param {string} params.response_message - Agent's response
 * @param {string} [params.agent_name]
 */
export const sendInquiryResponseEmail = async (params) => {
  const { user_id, client_email } = params;
  if (!client_email) return { success: false, skipped: true };
  if (user_id && !(await isEmailAllowed(user_id, 'transactional'))) {
    console.info('[ResendEmail] Inquiry response email skipped — user preference off');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'inquiry_response', params);
};

// ─── NOTAIRE DOSSIER EMAILS ────────────────────────────────────────────────────

/**
 * Send email to buyer and/or seller when notaire requests a missing document.
 * @param {object} params
 * @param {string} [params.buyer_name]
 * @param {string} [params.buyer_email]
 * @param {string} [params.seller_name]
 * @param {string} [params.seller_email]
 * @param {string} [params.notaire_name]
 * @param {string} [params.dossier_reference]
 * @param {string} [params.property_title]
 * @param {string} params.document_name
 * @param {string} [params.document_type]
 * @param {string} [params.notaire_notes]
 */
export const sendNotaireMissingDocumentEmail = async (params) => {
  if (!params?.buyer_email && !params?.seller_email) {
    console.info('[ResendEmail] Missing document email skipped — no recipient');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'notaire_missing_document', params);
};

/**
 * Send email to buyer and/or seller when notaire rejects a dossier.
 * @param {object} params
 * @param {string} [params.buyer_name]
 * @param {string} [params.buyer_email]
 * @param {string} [params.seller_name]
 * @param {string} [params.seller_email]
 * @param {string} [params.notaire_name]
 * @param {string} [params.dossier_reference]
 * @param {string} [params.property_title]
 * @param {string} [params.rejection_reason]
 */
export const sendNotaireDossierRejectedEmail = async (params) => {
  if (!params?.buyer_email && !params?.seller_email) {
    console.info('[ResendEmail] Dossier rejected email skipped — no recipient');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'notaire_dossier_rejected', params);
};

/**
 * Send email to buyer and/or seller when notaire validates dossier conformity.
 * @param {object} params
 * @param {string} [params.buyer_name]
 * @param {string} [params.buyer_email]
 * @param {string} [params.seller_name]
 * @param {string} [params.seller_email]
 * @param {string} [params.notaire_name]
 * @param {string} [params.dossier_reference]
 * @param {string} [params.property_title]
 * @param {string} [params.validated_at]
 */
export const sendNotaireDossierValidatedEmail = async (params) => {
  if (!params?.buyer_email && !params?.seller_email) {
    console.info('[ResendEmail] Dossier validated email skipped — no recipient');
    return { success: false, skipped: true };
  }
  return callEdgeFunction(TRANSACTIONAL_URL, 'notaire_dossier_validated', params);
};

export default {
  sendAppointmentConfirmationEmail,
  sendPropertyInquiryEmail,
  sendNotaryAppointmentEmail,
  sendAppointmentReminderEmail,
  sendWishlistSavedEmail,
  sendPropertyUpdateEmail,
  sendPromotionalCampaignEmail,
  sendBatchPromotionalCampaign,
  sendApplicationDecisionEmail,
  sendAppointmentRescheduledEmail,
  sendAppointmentStatusUpdateEmail,
  sendInquiryResponseEmail,
  sendNotaireMissingDocumentEmail,
  sendNotaireDossierRejectedEmail,
  sendNotaireDossierValidatedEmail,
  isEmailAllowed,
  getUserCommunicationSettings,
  getEmailFrequency,
};
